import { ExpressionValidationResultDc } from "@evergis/api";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ComparisonOperator } from "@evergis/ui";

import { updateLayerMeta } from "../../../ducks/project/project";
import { api } from "../../../evergis/api";
import { FilterCondition } from "../../../evergis/core/condition/FilterCondition";
import { useLayerUpdate } from "../../../hooks/layer/useLayerUpdate";
import { useNotification } from "../../../hooks/notifications/useNotification";
import { isObject } from "../../../utils/isObject";
import { createConditionString } from "../../../evergis/core/condition/createCondition";
import { useSGisLayer } from "../../../hooks/sgis/useSGisLayers";
import { useSGisLayerRedraw } from "../../../hooks/sgis/useSGisLayerRedraw";

import { FilterConditionOperation, FilterConditionType } from "../types";
import { LayerInfo } from "../../../interfaces/resourceInfo";

// eslint-disable-next-line max-lines-per-function
export const useLayerFilterUpdate = (
  layer: LayerInfo,
  condition: string,
  conditions: FilterConditionType[],
  conditionOperation: FilterConditionOperation,
  options: { devMode: boolean },
): { filter: () => Promise<ExpressionValidationResultDc>; loading: boolean; serverCondition: string | null } => {
  const { t } = useTranslation("layerFilter");
  const dispatch = useDispatch();
  const sgisLayer = useSGisLayer(layer.name);
  const redrawLayer = useSGisLayerRedraw(sgisLayer);
  const [update, { updating, error, updatedLayer }] = useLayerUpdate(layer);
  const [serverValidationError, setServerValidationError] = useState();

  const validate = useCallback(
    (expression: string) => {
      return api.layers.validateExpression({ layerName: layer?.name, expression });
    },
    [layer?.name],
  );

  const isComplex = useMemo(() => {
    return conditions?.filter(item => isObject(item.attribute) || isObject(item.value)).length > 0;
  }, [conditions]);

  const newCondition: string = useMemo(() => {
    return options.devMode || (!options.devMode && isComplex)
      ? condition
      : FilterCondition.from(conditions, conditionOperation).toString();
  }, [options, isComplex, condition, conditions, conditionOperation]);

  const formatServerCondition = useMemo(() => {
    if (options.devMode || (!options.devMode && isComplex)) {
      return newCondition;
    }

    return (
      conditions
        ?.filter(
          ({ comparisonOperator, value }) =>
            ![ComparisonOperator.Empty, ComparisonOperator.Filled].includes(comparisonOperator) &&
            value !== null &&
            value !== undefined,
        )
        .map(item => {
          const isBetween = item.comparisonOperator === ComparisonOperator.Between;
          const isOutside = item.comparisonOperator === ComparisonOperator.Outside;

          if (typeof item.value === "string" && (isBetween || isOutside)) {
            const conditionValue = item.value.split(" - ");

            return `${isOutside ? "!" : ""}between(${item.attribute}, ${conditionValue[0]}, ${conditionValue[1]})`;
          }

          return createConditionString(item);
        })
        .join(` ${conditionOperation} `) || null
    );
  }, [conditionOperation, conditions, isComplex, newCondition, options.devMode]);

  const applyConditionToLayer = useCallback(() => {
    const { name } = layer;

    dispatch(
      updateLayerMeta({
        name,
        condition: null,
      }),
    );

    return update({
      condition: formatServerCondition,
    }).then(() => redrawLayer());
  }, [layer, formatServerCondition, options, isComplex, dispatch, update, sgisLayer]); // eslint-disable-line

  const filter = useCallback(async () => {
    if (!newCondition) {
      return applyConditionToLayer();
    }

    setServerValidationError(null);

    let validationResult;

    try {
      validationResult = await validate(formatServerCondition);

      if (validationResult && validationResult.isValid && !updatedLayer) {
        await applyConditionToLayer();
      }
    } catch (e) {
      setServerValidationError(e);
    }

    return validationResult;
  }, [formatServerCondition, newCondition, validate, applyConditionToLayer, updatedLayer]);

  useNotification(error || serverValidationError, {
    error: true,
    title: t("filterSaveError"),
    description: t("tryLater"),
  });

  useNotification(updatedLayer, {
    success: true,
    title: t("filterSuccessfullySaved"),
  });

  return {
    filter,
    loading: updating,
    serverCondition: formatServerCondition,
  };
};
